import {EVideoProviders, IProductVideo} from './IProduct';
import {IThronAsset} from '../thron/thronUtils';

import {EProviders} from 'brandComponents/core/Video/IVideoProps';

/**
 * Shifts videos between the given positions
 * @param {IProductVideo} videos - videos array
 * @param {Number} from - from
 * @param {Number} to - to
 */
const shiftVideoPosition = (videos: IProductVideo[], from: number, to: number): void => {
	if (from < to) {
		videos.filter((v) => v.position > from && v.position <= to).forEach((v) => v.position--);
	} else {
		videos.filter((v) => v.position >= to && v.position < from).forEach((v) => v.position++);
	}
};

const transformVideoProvider = (provider: string): EVideoProviders => {
	switch (provider.toLowerCase()) {
		case 'thron':
			return EVideoProviders.THRON;
		case 'vimeo':
			return EVideoProviders.VIMEO;
		case 'cloudflare':
			return EVideoProviders.CLOUDFLARE_STREAM;
	}
};

/**
 * Parses a comma-separated video string (each item: "videoId" or "videoId:position")
 * into an array of IProductVideo, sorted by position.
 *
 * @param raw - The raw string to parse
 * @param provider - The video provider to assign
 */
export const parseVideoString = (raw: string, provider: EVideoProviders): IProductVideo[] => {
	// The position starts from 1, while we compare it with an array that starts from 0, so we always calculate -1.
	const positionOffset = -1;

	return raw
		.trim()
		.split(/\s*,+\s*/)
		.filter((i) => !!i)
		.map((pair) => {
			const [id, position = null] = pair.split(/\s*:+\s*/);
			let videoId = id;
			let videoProvider = provider;

			if (id.includes('@')) {
				videoId = id.split('@')[1];
				videoProvider = transformVideoProvider(id.split('@')[0]);
			}

			return {
				videoId: videoId,
				position: position ? parseInt(position, 10) + positionOffset : null,
				provider: videoProvider,
			};
		})
		.sort((a, b) => a.position - b.position);
};

/**
 * Extracts video entries from a Thron assets array.
 */
export const extractThronAssetVideos = (thronAssets: IThronAsset[]): IProductVideo[] => {
	return thronAssets
		.map((a, i) => ({
			videoId: a.type === 'video' ? a.id : undefined,
			position: i,
			provider: EVideoProviders.THRON_ASSET,
		}))
		.filter((a) => !!a.videoId);
};

const getBaseVideoId = (videoId: string): string => videoId.split('/')[0];

/**
 * Builds the PLP video list by merging c_plpProductVideos with Thron asset videos,
 * avoiding duplicates and resolving position conflicts.
 */
export const buildPlpVideos = (plpProductVideosStr: string | undefined, thronAssets: IThronAsset[]): IProductVideo[] => {
	const plpProductVideos = plpProductVideosStr ? parseVideoString(plpProductVideosStr, EVideoProviders.THRON) : [];
	let thronVideos = extractThronAssetVideos(thronAssets);

	plpProductVideos.forEach((pv) => {
		const pvBaseId = getBaseVideoId(pv.videoId);
		const existingVideo = thronVideos.find((ev) => pvBaseId === getBaseVideoId(ev.videoId));

		if (existingVideo) {
			if (typeof pv.position === 'number' && existingVideo.position !== pv.position) {
				shiftVideoPosition(thronVideos, existingVideo.position, pv.position);

				existingVideo.position = pv.position;
			}
		} else {
			thronVideos.push(pv);
		}
	});

	return thronVideos.sort((a, b) => a.position - b.position);
};

/**
 * Maps a video provider to a brand component provider
 * @param {EVideoProviders} provider - The video provider
 * @returns {EProviders} The brand component provider
 */
export const mapVideoProviderToProvider = (provider?: EVideoProviders): EProviders => {
	if (!provider) {
		return EProviders.CLOUDFLARE_STREAM;
	}

	switch (provider) {
		case EVideoProviders.CLOUDFLARE_STREAM:
			return EProviders.CLOUDFLARE_STREAM;
		case EVideoProviders.VIMEO:
			return EProviders.VIMEO;
		case EVideoProviders.THRON:
		case EVideoProviders.THRON_ASSET:
			return EProviders.THRON;
		default:
			return EProviders.CLOUDFLARE_STREAM;
	}
};
